import { useRouter } from 'next/router'
import { useRef } from 'react'
import mapSliceToComponent from '~/imports/product_page'
import { STORYBLOK_TYPES } from '~/lib/storyblok-types'

import { ProductData } from '@unlikelystudio/react-abstract-components'

import SliceManager from '~/components/Abstracts/SliceManager'
import Tracking from '~/components/Abstracts/Tracking'
import ProductBigParagraph from '~/components/UI/ProductBigParagraph'
import ProductHeader from '~/components/UI/ProductHeader'
import { ProductRecommendations } from '~/components/UI/ProductRecommendations'
import ProductReviewsList from '~/components/UI/ProductReviewsList'
import ProductTwoColsWYSIWYG from '~/components/UI/ProductTwoColsWYSIWYG'

import PackProductsProvider from '~/providers/PackProductsProvider'
import ProductPageProvider from '~/providers/ProductPageProvider'
import ShopifyProductProvider from '~/providers/ShopifyProductProvider'
import UserSelectionProvider from '~/providers/UserSelectionProvider'

import useCurrency from '~/hooks/useCurrency'

import serializeJSONLD from '~/data/json-ld/product/serialize-json-ld'
import { getStaticPathsFunction } from '~/data/page-data'
import getProductPageStaticPropsFunction from '~/data/product-page-data'
import { ProductPageData } from '~/data/product-page-data/serializer'

export default function ProductFashion({
  slices,
  productHeader,
  productBigParagraph,
  productTwoColsWYSIWYG,
  jsonLD,
  recommendations,
  reviews,
}: ProductPageData) {
  const router = useRouter()
  const productReviewsListRef = useRef(null)
  const currency = useCurrency()

  if (!productHeader?.shopifyProduct?.shopifyId) return null

  return (
    <>
      <link rel="stylesheet" href="https://use.typekit.net/tec0ria.css" />
      <link
        rel="preload"
        href="/fonts/ChronicleDisplay/ChronicleDisplay-XLight.woff2"
        as="font"
        type="font/woff2"
        crossOrigin="anonymous"
      />
      <ProductData
        {...serializeJSONLD({
          name: productHeader?.productName ?? null,
          description: jsonLD?.description ?? null,
          image: [productHeader?.shopifyProduct?.defaultVariant?.image],
          price:
            productHeader?.shopifyProduct?.defaultVariant?.price?.amount ??
            null,
          path: router?.asPath,
          currency,
          sku: productHeader?.shopifyProduct?.defaultVariant?.sku ?? null,
          availability:
            productHeader?.shopifyProduct?.defaultVariant?.availableForSale ??
            null,
        })}
      />
      <ShopifyProductProvider {...productHeader}>
        <PackProductsProvider {...productHeader}>
          <UserSelectionProvider {...productHeader}>
            <ProductPageProvider {...productHeader}>
              <ProductHeader
                productReviewsListRef={productReviewsListRef}
                {...productHeader}
              />
            </ProductPageProvider>
          </UserSelectionProvider>
        </PackProductsProvider>
      </ShopifyProductProvider>
      {productBigParagraph && <ProductBigParagraph {...productBigParagraph} />}
      {productTwoColsWYSIWYG && (
        <ProductTwoColsWYSIWYG {...productTwoColsWYSIWYG} />
      )}
      <SliceManager
        mapSliceTypeToComponent={mapSliceToComponent}
        slicesData={slices}
        withContextPosition={false}
      />
      {reviews && (
        <ProductReviewsList innerRef={productReviewsListRef} {...reviews} />
      )}
      {recommendations?.length > 0 && (
        <ProductRecommendations items={recommendations} />
      )}
      <Tracking template="product" />
    </>
  )
}

export const getStaticProps = getProductPageStaticPropsFunction()

export const getStaticPaths = getStaticPathsFunction({
  type: STORYBLOK_TYPES.PRODUCT,
})
